import {LivePreviewProvider} from '@tlg-contenthub/live-preview/react';
import React, {ReactNode, useEffect} from 'react';
import {stripPreviewConnectionParams} from '@tlg-contenthub/live-preview';

import {useEntryInspectorToggle, useRequestClass} from 'coreHooks';
import {getLivePreviewProviderOptions} from 'coreUtils/livePreview';

interface CmsPreviewProviderProps {
	children: ReactNode;
}

/**
 * Wraps the app in the CMS `LivePreviewProvider` when the page is opened in live or CMS
 * preview, with the entry inspector (if toggled on) and the preview badge.
 * Outside preview `children` are rendered as they are.
 * In live preview it drops the CMS connection parameters (and their token) from the address once the server
 * has read them.
 */
const CmsPreviewProvider = ({children}: CmsPreviewProviderProps): React.ReactElement => {
	const requestClass = useRequestClass();
	const [isEntryInspectorOn] = useEntryInspectorToggle();

	useEffect(() => {
		if (!requestClass.isLivePreview) {
			return;
		}

		const url = stripPreviewConnectionParams(window.location.href);

		if (url !== window.location.href) {
			window.history.replaceState(window.history.state, '', url);
		}
	}, [requestClass.isLivePreview]);

	if (!requestClass.isLivePreview && !requestClass.isCmsPreview) {
		return <>{children}</>;
	}

	return (
		<LivePreviewProvider
			{...getLivePreviewProviderOptions()}
			enableInspector={isEntryInspectorOn}
			showPreviewBadge
		>
			{children}
		</LivePreviewProvider>
	);
};

export default CmsPreviewProvider;
